import { useWorkspaceId } from "../../hooks/useWorkspaceId";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "@tanstack/react-store";
import { useTranslation } from "react-i18next";
import { store } from "../../../store/store";
import { getQueryStringFromParams } from "../utils/paramUtils";
import { useHttpTaxManagerAppHeaders } from "../utils/useHttpTaxManagerAppHeaders";
import { BASE_API_URL, HttpError } from "../utils/useTaxManagerAppQueryClient";
import type { FulfilmentsDto } from "./FulfilmentsDto";

export function useGetFulfilments() {
    const { t } = useTranslation();
    const workspaceId = useWorkspaceId();
    const headers = useHttpTaxManagerAppHeaders();
    const filters = useSelector(
        store,
        (state) => state.fulfilmentSearchFilters,
    );

    const fulfilmentName = filters.fulfilmentName
        ?.trim()
        .replaceAll(/\s{2,}/g, " ");

    const queryParams = {
        deadlineFrom: filters.deadlineFrom.getTime(),
        deadlineTo: filters.deadlineTo.getTime(),
        ...(fulfilmentName ? { fulfilmentName } : {}),
        ...(filters.statuses.length > 0 ? { statuses: filters.statuses } : {}),
        ...(filters.taxPayerWorkspaceId
            ? { taxPayerWorkspaceId: filters.taxPayerWorkspaceId }
            : {}),
        withWarnings: filters.withWarnings || false,
        ...(filters.expired !== undefined ? { expired: filters.expired } : {}),
    };

    const query = useQuery<FulfilmentsDto>({
        queryKey: ["useGetFulfilments", workspaceId, filters],
        queryFn: async () => {
            const queryString = getQueryStringFromParams(queryParams);
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/fulfilments?${queryString}`,
                { headers },
            );

            if (!response.ok) {
                throw new HttpError(response);
            }

            return response.json();
        },
        // Without the guard the query would fire against /tenants/undefined/...
        // and run again once the workspace lands (see useWorkspaceId).
        enabled: !!workspaceId,
        initialData: { fulfilments: {}, taxPayers: [] },
        placeholderData: () => ({ fulfilments: {}, taxPayers: [] }),
        meta: {
            resourceName: t("errors.resourceName.fulfilments"),
        },
    });

    return query;
}
